import React from 'react';
import { Link } from 'react-router-dom';

const quickLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Our Menu', path: '/services' },
  { name: 'Contact', path: '/contact' },
];

const hours = [
  { day: 'Monday – Friday', time: '8:00 AM – 10:00 PM' },
  { day: 'Saturday', time: '8:00 AM – 10:00 PM' },
  { day: 'Sunday', time: '8:00 AM – 10:00 PM' },
];

export default function Footer() {
  return (
    <footer className="bg-warm-gradient text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-amber-400/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-400/5 rounded-full translate-x-1/3 translate-y-1/3" />

      <div className="container-custom relative z-10">
        {/* Main Footer */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 py-16 border-b border-white/10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <img
                src="/images/logo.png"
                alt="Squeeze N Sip"
                className="h-12 w-12 rounded-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div>
                <h3
                  className="text-xl font-bold text-white leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Squeeze N Sip
                </h3>
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">EST 2024</span>
              </div>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Your cozy neighborhood café serving premium coffee, trendy shakes, healthy bites, 
              and sweet treats since 2024.
            </p>
            {/* Social Media */}
            <div className="flex gap-3">
              <a
                href="#"
                id="footer-facebook"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-amber-400 hover:text-white transition-all duration-300"
                aria-label="Facebook"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                </svg>
              </a>
              <a
                href="#"
                id="footer-instagram"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-amber-400 hover:text-white transition-all duration-300"
                aria-label="Instagram"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </a>
              <a
                href="#"
                id="footer-linkedin"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-amber-400 hover:text-white transition-all duration-300"
                aria-label="LinkedIn"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-amber-400 mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-amber-400 transition-colors duration-300 text-sm flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-amber-400 transition-all duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-amber-400 mb-6">
              Business Hours
            </h4>
            <ul className="space-y-3">
              {hours.map((item) => (
                <li key={item.day} className="flex flex-col">
                  <span className="text-white/80 text-sm font-medium">{item.day}</span>
                  <span className="text-white/50 text-sm">{item.time}</span>
                </li>
              ))}
            </ul>
            {/* Open Now badge */}
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/15 border border-green-500/20">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-green-400 text-xs font-medium">Open Now</span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-amber-400 mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:7993305226"
                  className="flex items-start gap-3 text-white/60 hover:text-amber-400 transition-colors duration-300"
                >
                  <span className="text-lg mt-0.5">📱</span>
                  <div>
                    <span className="text-xs text-white/40 uppercase tracking-wider">Phone</span>
                    <p className="text-sm">7993305226</p>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href="mailto:akulamadhuhasini@gmail.com"
                  className="flex items-start gap-3 text-white/60 hover:text-amber-400 transition-colors duration-300"
                >
                  <span className="text-lg mt-0.5">✉️</span>
                  <div>
                    <span className="text-xs text-white/40 uppercase tracking-wider">Email</span>
                    <p className="text-sm break-all">akulamadhuhasini@gmail.com</p>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/60">
                  <span className="text-lg mt-0.5">📍</span>
                  <div>
                    <span className="text-xs text-white/40 uppercase tracking-wider">Address</span>
                    <p className="text-sm">Near Kartigi Bus Stop</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs">
            © {new Date().getFullYear()} Squeeze N Sip. All rights reserved.
          </p>
          <p className="text-white/30 text-xs">
            Crafted with ☕ & ❤️
          </p>
        </div>
      </div>
    </footer>
  );
}
